import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

export type QueryHistoryItem = {
  /**
   * The appId
   */
  appId: string
  /**
   * The query string
   */
  query: string
  /**
   * When the query was first added to the history
   * (not used for anything right now, but worth keeping)
   */
  createdAt: number
  /**
   * When the query was last used
   */
  lastUsed: number
  /**
   * Whether the query is a favorite
   */
  isFavorite: boolean
  /**
   * The active view the query was executed in
   */
  activeView?: 'collections' | 'editor'
}

interface QueryHistoryStore {
  /**
   * The queries in the history
   */
  queries: QueryHistoryItem[]
  /**
   * Add a query to the history
   */
  addQueryToHistory: (query: QueryHistoryItem) => void
  /**
   * Update the last used time for a query
   */
  updateLastUsedForQuery: (
    query: string,
    activeView: 'collections' | 'editor' | undefined,
  ) => void
  /**
   * Remove a query from the history
   */
  removeQueryFromHistory: (query: QueryHistoryItem) => void
  /**
   * Toggle isFavorite for a query
   */
  toggleFavorite: (query: QueryHistoryItem) => void
}

export const useQueryHistoryStore = create<QueryHistoryStore>()(
  persist(
    (set, get) => ({
      queries: [],

      addQueryToHistory: (query: QueryHistoryItem) => {
        if (!query?.appId || !query?.query) return

        // First, check if the query already exists
        const existingQuery = get().queries.find((q) => q.query === query.query)

        // If the query exists, update the last used time
        if (existingQuery) {
          get().updateLastUsedForQuery(query.query, query?.activeView)
          return
        }

        // If the query doesn't exist, add it to the history
        set((old) => ({
          queries: [...old.queries, query],
        }))
      },

      updateLastUsedForQuery: (
        query: string,
        activeView?: 'collections' | 'editor',
      ) => {
        set((old) => ({
          queries: old.queries.map((q) =>
            q.query === query
              ? {
                  ...q,
                  lastUsed: new Date().getTime(),
                  // Only add activeView if it is defined
                  ...(!!activeView ? { activeView } : {}),
                }
              : q,
          ),
        }))
      },

      removeQueryFromHistory: (query: QueryHistoryItem) =>
        set((old) => ({
          queries: old.queries.filter((q) => q !== query),
        })),

      toggleFavorite: (query: QueryHistoryItem) =>
        set((old) => ({
          queries: old.queries.map((q) =>
            q === query ? { ...q, isFavorite: !q.isFavorite } : q,
          ),
        })),
    }),
    {
      name: 'query-history',
      storage: createJSONStorage(() => localStorage),
      version: 1,
    },
  ),
)

// Helpers that split out the queries and favorites, and sorts them
export const useQueryHistory = (appId: string) =>
  useQueryHistoryStore((state) => {
    return state.queries
      .filter((q) => q.appId === appId)
      .sort((a, b) => b.lastUsed - a.lastUsed)
  })

export const useQueryFavorites = (appId: string) =>
  useQueryHistoryStore((state) =>
    state.queries
      .filter((q) => q.isFavorite && q.appId === appId)
      .sort((a, b) => b.lastUsed - a.lastUsed),
  )

interface StrictModeSetting {
  /**
   * Whether the strict mode is enabled
   */
  enabled: boolean
  /**
   * Toggle the strict mode setting
   */
  toggleEnabled: () => void
}

export const useStrictModeSetting = create<StrictModeSetting>()(
  persist(
    (set, get) => ({
      enabled: false,

      toggleEnabled: () => set(() => ({ enabled: !get().enabled })),
    }),
    {
      name: 'strict-mode-setting',
      storage: createJSONStorage(() => localStorage),
      version: 1,
    },
  ),
)

interface CollectionFilterSettings {
  /**
   * Whether to hide empty collections (default: true)
   */
  hideEmptyCollections: boolean
  /**
   * Toggle the hide empty collections setting
   */
  toggleHideEmptyCollections: () => void
  /**
   * The limit for the number of documents to show in the collections list (default: 200)
   */
  documentsLimit: number
  /**
   * Set the documents limit
   */
  setDocumentsLimit: (limit: number) => void
  /**
   * Whether to hide internal collections (default: true)
   */
  hideInternalCollections: boolean
  /**
   * Toggle the hide internal collections setting
   */
  toggleHideInternalCollections: () => void
}

export const useCollectionFilterSettings = create<CollectionFilterSettings>()(
  persist(
    (set, get) => ({
      hideEmptyCollections: true,
      documentsLimit: 200,
      hideInternalCollections: true,

      toggleHideEmptyCollections: () =>
        set(() => ({ hideEmptyCollections: !get().hideEmptyCollections })),

      setDocumentsLimit: (limit: number) =>
        set(() => ({ documentsLimit: limit })),

      toggleHideInternalCollections: () =>
        set(() => ({
          hideInternalCollections: !get().hideInternalCollections,
        })),
    }),
    {
      name: 'collection-filter-settings',
      storage: createJSONStorage(() => localStorage),
      version: 1,
    },
  ),
)
